import axiosInstance from './axiosInstance'
import { useAuthStore } from '@/app/store/authStore'

// PDF Operations - Use browser APIs
export const pdfApi = {
  saveSalesOrderPDF: async () => {
    // This will be handled by the component using @react-pdf/renderer
    // The component will generate the PDF and trigger download
    console.log('PDF save will be handled by component')
  },
  
  printSalesOrderPDF: async (html: string) => {
    // Open in new tab and print
    const printWindow = window.open('', '_blank')
    if (printWindow) {
      printWindow.document.write(html)
      printWindow.document.close()
      printWindow.print()
    }
  },
  
  previewSalesOrderPDF: async (html: string) => {
    // Open in new tab for preview
    const previewWindow = window.open('', '_blank')
    if (previewWindow) {
      previewWindow.document.write(html)
      previewWindow.document.close()
    }
  },
  
  saveSalesInvoicePDF: async () => {
    console.log('PDF save will be handled by component')
  },
  
  printSalesInvoicePDF: async (html: string) => {
    const printWindow = window.open('', '_blank')
    if (printWindow) {
      printWindow.document.write(html)
      printWindow.document.close()
      printWindow.print()
    }
  },
  
  previewSalesInvoicePDF: async (html: string) => {
    const previewWindow = window.open('', '_blank')
    if (previewWindow) {
      previewWindow.document.write(html)
      previewWindow.document.close()
    }
  }
}

// Thermal Printer Operations - Use localStorage and browser print
export const thermalPrinterApi = {
  getPrinters: async () => {
    // In web, we can't get printer list. Return empty array
    return []
  },
  
  printReceipt: async (data: any) => {
    // Generate HTML and use window.print()
    const printWindow = window.open('', '_blank', 'width=300,height=600')
    if (printWindow) {
      printWindow.document.write(data.html || data)
      printWindow.document.close()
      printWindow.print()
    }
  },
  
  getSettings: async () => {
    // Get from localStorage
    const settings = localStorage.getItem('thermalPrinterSettings')
    return settings ? JSON.parse(settings) : null
  },
  
  saveSettings: async (settings: any) => {
    // Save to localStorage
    localStorage.setItem('thermalPrinterSettings', JSON.stringify(settings))
    return { success: true }
  }
}

// Database/API Operations
export const dbApi = {
  setAuthToken: (token: string) => {
    // Store in auth store (which uses localStorage)
    useAuthStore.getState().setToken(token)
  },
  
  searchItems: async (query: string) => {
    try {
      const response = await axiosInstance.get('/api/items/search', {
        params: { q: query }
      })
      return response.data
    } catch (error) {
      console.error('Search items error:', error)
      return []
    }
  },
  
  syncData: async () => {
    // Not needed in web version
    console.log('Sync not needed in web version')
    return { success: true }
  },
  
  getItemPriceDetails: async (itemId: string, siteId?: string) => {
    try {
      const response = await axiosInstance.get(`/api/items/${itemId}/price-details`, {
        params: { site_id: siteId }
      })
      return response.data
    } catch (error) {
      console.error('Get item price details error:', error)
      return null
    }
  },
  
  executeDirectQuery: async () => {
    // Security risk - not implementing
    throw new Error('Direct queries not supported in web version')
  },
  
  getTaxRateById: async (taxRateId: string) => {
    try {
      const response = await axiosInstance.get(`/api/tax-rates/${taxRateId}`)
      return response.data
    } catch (error) {
      console.error('Get tax rate error:', error)
      return null
    }
  },
  
  getItemSalePrice: async (itemId: string, siteId?: string, quantity?: number) => {
    try {
      const response = await axiosInstance.get(`/api/items/${itemId}/sale-price`, {
        params: { site_id: siteId, quantity }
      })
      return response.data
    } catch (error) {
      console.error('Get item sale price error:', error)
      return null
    }
  },
  
  // Draft Invoice Operations - Convert to regular API calls
  createDraftInvoice: async (data: any) => {
    try {
      const response = await axiosInstance.post('/api/draft-invoices', data)
      return response.data
    } catch (error) {
      console.error('Create draft invoice error:', error)
      throw error
    }
  },
  
  getDraftInvoices: async () => {
    try {
      const response = await axiosInstance.get('/api/draft-invoices')
      return response.data
    } catch (error) {
      console.error('Get draft invoices error:', error)
      return []
    }
  },
  
  getDraftInvoice: async (id: string) => {
    try {
      const response = await axiosInstance.get(`/api/draft-invoices/${id}`)
      return response.data
    } catch (error) {
      console.error('Get draft invoice error:', error)
      return null
    }
  },
  
  updateDraftInvoice: async (id: string, data: any) => {
    try {
      const response = await axiosInstance.put(`/api/draft-invoices/${id}`, data)
      return response.data
    } catch (error) {
      console.error('Update draft invoice error:', error)
      throw error
    }
  },
  
  deleteDraftInvoice: async (id: string) => {
    try {
      await axiosInstance.delete(`/api/draft-invoices/${id}`)
      return { success: true }
    } catch (error) {
      console.error('Delete draft invoice error:', error)
      throw error
    }
  },
  
  convertDraftToInvoice: async (id: string) => {
    try {
      const response = await axiosInstance.post(`/api/draft-invoices/${id}/convert`, {})
      return response.data
    } catch (error) {
      console.error('Convert draft invoice error:', error)
      throw error
    }
  }
}

// Tab Management - Use browser tabs
export const tabsApi = {
  create: (url: string, title?: string) => {
    window.open(url, '_blank')
    return { id: Date.now().toString(), url, title }
  },
  
  switch: () => {
    // Browser handles tab switching
    console.log('Tab switching handled by browser')
  },
  
  close: () => {
    window.close()
  },
  
  getAll: () => {
    // Can't get all browser tabs
    return []
  },
  
  updateTitle: (title: string) => {
    document.title = title
  },
  
  navigate: (url: string) => {
    window.location.href = url
  },
  
  // Event listeners not needed for browser tabs
  onTabsChanged: () => {},
  onActiveTabChanged: () => {}
}

// Main API object to replace window.api
export const webApi = {
  pdf: pdfApi,
  thermalPrinter: thermalPrinterApi,
  db: dbApi,
  tabs: tabsApi
}